<script lang="ts">
    import { VpUserIcon } from '@decathlon-experience/icons/svelte';
    import dayjs, { type Dayjs } from 'dayjs';

    import { trans } from '$assets/shop-translator.js';
    import cn from '$common/utils/cn';
    import Button from '$saga/components/Button.svelte';
    import { formatCurrency } from '$saga/utils/price';
    import type {
        MinPriceMonth,
        SnowtrexAccommodationDate,
        SnowtrexMonthlyPrices,
    } from '$saga/views/Tour/VisibleDates/types';
    import { getQuantileSizeClass } from '$saga/views/Tour/VisibleDates/utils';

    import {
        getCheapestSnowtrexPrice,
        getSnowtrexMonthPrices,
        getSnowtrexMonthQuantile,
    } from './utils';

    export let durations: number[];
    export let availablePax: number[];
    export let snowtrexAccommodationDates: SnowtrexAccommodationDate[] = [];
    export let cheapestMonthlyPrices: SnowtrexMonthlyPrices = {};
    export let selectedDuration: number | null;
    export let selectedPax: number;
    export let selectedMonth: Dayjs | null = null;

    $: minPricePerMonths = getSnowtrexMonthPrices(
        snowtrexAccommodationDates,
        selectedPax,
        selectedDuration,
    ).map(
        ({ month, price }): MinPriceMonth => ({
            date: dayjs(month),
            price,
            quantile: getSnowtrexMonthQuantile(cheapestMonthlyPrices, month),
        }),
    );

    $: cheapestOverallPrice = getCheapestSnowtrexPrice(
        snowtrexAccommodationDates,
        selectedPax,
        selectedDuration,
    );
</script>

<h3 class="pb-m font-semibold">
    {trans('tour.dates.snowtrex.customize')}
</h3>
<div class="mb-l flex flex-col gap-m">
    <div class="flex flex-col gap-m desktop:flex-row desktop:flex-wrap">
        <!-- Duration filter -->
        <div class="flex flex-col gap-xs">
            <span class="whitespace-nowrap text-xs text-content-quiet">
                {trans('tour.dates.snowtrex.duration_label')}
            </span>
            <div class="flex flex-wrap gap-xs">
                {#each durations as duration (duration)}
                    <Button
                        type="button"
                        size="small"
                        intent="secondary"
                        shape="rounded"
                        class={cn(
                            'capitalize',
                            selectedDuration === duration &&
                                'border-blue-brand bg-container-brand text-white',
                        )}
                        on:click={() => (selectedDuration = duration)}
                    >
                        {duration}
                        {trans('tour.dates.snowtrex.nights')}
                    </Button>
                {/each}
            </div>
        </div>

        <hr class="h-stretch w-px bg-container-quiet" />

        <div class="flex flex-col gap-xs">
            <span class="whitespace-nowrap text-xs text-content-quiet">
                {trans('tour.dates.snowtrex.pax_label')}
            </span>
            <div class="flex flex-wrap gap-xs">
                {#each availablePax as pax (pax)}
                    <Button
                        type="button"
                        size="small"
                        intent="secondary"
                        shape="rounded"
                        class={cn(
                            selectedPax === pax &&
                                'border-blue-brand bg-container-brand text-white',
                        )}
                        on:click={() => (selectedPax = pax)}
                    >
                        {#if selectedPax === pax}
                            <VpUserIcon class="size-m" />
                        {/if}
                        {pax}
                    </Button>
                {/each}
            </div>
        </div>
    </div>

    <hr class="w-full" />

    <!-- months filter -->
    {#if minPricePerMonths.length > 0}
        <div class="flex flex-col gap-xs">
            <span class="text-xs text-content-quiet">
                {trans('tour.dates.snowtrex.month_label')}
            </span>
            <div class="flex flex-wrap gap-xs">
                <Button
                    type="button"
                    class={cn(
                        'capitalize',
                        selectedMonth === null &&
                            'border-blue-brand bg-container-brand text-white',
                    )}
                    shape="rounded"
                    size="small"
                    intent="secondary"
                    on:click={() => (selectedMonth = null)}
                >
                    <span class="flex flex-col gap-2xs">
                        {trans('tour.dates.snowtrex.all_months')}
                        {#if cheapestOverallPrice !== null}
                            <span class="text-xs font-semibold">
                                {formatCurrency(cheapestOverallPrice, false)}
                            </span>
                        {/if}
                    </span>
                </Button>
                {#each minPricePerMonths as minPriceMonth (minPriceMonth.date.format('YYYY-MM'))}
                    {@const isSelectedMonth = selectedMonth?.isSame(
                        minPriceMonth.date,
                        'month',
                    )}
                    <Button
                        type="button"
                        shape="rounded"
                        size="small"
                        intent="secondary"
                        class={cn(
                            isSelectedMonth &&
                                'border-blue-brand bg-container-brand text-white',
                        )}
                        disabled={minPriceMonth.price == null}
                        on:click={() => (selectedMonth = minPriceMonth.date)}
                    >
                        <span class="flex flex-col gap-2xs capitalize">
                            {minPriceMonth.date.format('MMM YY')}
                            {#if minPriceMonth.price != null}
                                <span
                                    class={cn(
                                        'text-xs font-semibold',
                                        isSelectedMonth
                                            ? ' text-white'
                                            : getQuantileSizeClass(
                                                  minPriceMonth.quantile,
                                              ),
                                    )}
                                >
                                    {formatCurrency(minPriceMonth.price, false)}
                                </span>
                            {/if}
                        </span>
                    </Button>
                {/each}
            </div>
        </div>
    {/if}
</div>
